const normalizeName = value => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const getEntityId = (entity, keys) => {
  for (const key of keys) {
    if (entity?.[key]) return entity[key];
  }

  return null;
};

const normalizeLinkedInUrl = value => String(value || '').trim().replace(/\/+$/, '').toLowerCase();

const normalizeText = value => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const isClosedDeal = deal => {
  const status = String(deal?.status || '').trim().toLowerCase();

  return [
    'lukket',
    'tapt',
    'lost',
    'closed',
    'closed_lost',
    'closed_won',
    'vunnet',
    'won'
  ].includes(status);
};

const getErrorMessage = error => (
  error?.message ||
  (typeof error === 'string' ? error : '') ||
  'En uventet feil oppstod.'
);

const getDealNotes = deal => [
  deal?.notes,
  deal?.note,
  deal?.description,
  deal?.source
].filter(Boolean).join('\n');

export const hasOpenLinkedInOpportunity = ({
  prospect,
  deals = [],
  companies = [],
  contacts = []
}) => {
  if (!prospect) return false;

  const normalizedCompanyName = normalizeName(prospect.company_name);
  const normalizedContactName = normalizeName(prospect.full_name);
  const normalizedLinkedInUrl = normalizeLinkedInUrl(prospect.linkedin_url);

  const company = companies.find(item =>
    normalizeName(item.name) === normalizedCompanyName
  );
  const companyId = getEntityId(company, ['company_id', 'id']);

  const contact = contacts.find(item => {
    const sameLinkedInUrl = normalizedLinkedInUrl &&
      normalizeLinkedInUrl(item.linkedin_url) === normalizedLinkedInUrl;
    const sameNameAndCompany =
      normalizeName(item.name) === normalizedContactName &&
      getEntityId(item, ['company_id', 'companyId']) === companyId;

    return sameLinkedInUrl || sameNameAndCompany;
  });
  const contactId = getEntityId(contact, ['contact_id', 'id']);

  return deals.some(deal => {
    if (isClosedDeal(deal)) return false;

    const dealCompanyId = getEntityId(deal, ['company_id', 'companyId']);
    const dealContactId = getEntityId(deal, ['contact_id', 'contactId']);
    const dealCompanyName = normalizeName(
      deal.companyName || deal.company_name || deal.company
    );
    const dealContactName = normalizeName(
      deal.contactName || deal.contact_name || deal.contact
    );
    const dealText = normalizeText(getDealNotes(deal));
    const dealSource = normalizeText(deal.source);

    const sameCompanyAndContact =
      Boolean(companyId && contactId) &&
      dealCompanyId === companyId &&
      dealContactId === contactId;

    const sameCompanyAndContactName =
      Boolean(normalizedCompanyName && normalizedContactName) &&
      (
        (companyId && dealCompanyId === companyId) ||
        dealCompanyName === normalizedCompanyName
      ) &&
      (
        (contactId && dealContactId === contactId) ||
        dealContactName === normalizedContactName
      );

    const sameLinkedInUrl =
      Boolean(normalizedLinkedInUrl) &&
      dealText.includes(normalizedLinkedInUrl);

    const sameLinkedInProspect =
      dealSource.includes('linkedin outreach') &&
      dealContactName === normalizedContactName &&
      dealCompanyName === normalizedCompanyName;

    return (
      sameCompanyAndContact ||
      sameCompanyAndContactName ||
      sameLinkedInUrl ||
      sameLinkedInProspect
    );
  });
};

export const createLinkedInOpportunity = async ({
  prospect,
  dealInput,
  companies = [],
  contacts = [],
  deals = [],
  addCompany,
  addContact,
  addDeal
}) => {
  const companyName = prospect?.company_name?.trim();

  if (!companyName) {
    return { success: false, error: 'Prospektet mangler selskap.' };
  }

  if (!prospect?.full_name?.trim()) {
    return { success: false, error: 'Prospektet mangler navn.' };
  }

  try {
    if (hasOpenLinkedInOpportunity({
      prospect,
      deals,
      companies,
      contacts
    })) {
      return {
        success: false,
        duplicate: true,
        error: 'Det finnes allerede en salgsmulighet for dette prospektet.'
      };
    }

    const normalizedCompanyName = normalizeName(companyName);
    const existingCompany = companies.find(company =>
      normalizeName(company.name) === normalizedCompanyName
    );

    let company = existingCompany;
    let companyId = getEntityId(company, ['company_id', 'id']);

    if (!companyId) {
      const companyResult = await addCompany({
        name: companyName,
        status: 'Prospect',
        source: 'LinkedIn Outreach'
      });

      if (!companyResult?.success) {
        return {
          success: false,
          error: getErrorMessage(companyResult?.error) || 'Selskapet kunne ikke opprettes.'
        };
      }

      company = companyResult.data;
      companyId = getEntityId(company, ['company_id', 'id']);

      if (!companyId) {
        return { success: false, error: 'Selskapet ble opprettet uten gyldig ID.' };
      }
    }

    const normalizedLinkedInUrl = normalizeLinkedInUrl(prospect.linkedin_url);
    const normalizedName = normalizeName(prospect.full_name);

    const existingContact = contacts.find(contact => {
      const sameLinkedInUrl = normalizedLinkedInUrl &&
        normalizeLinkedInUrl(contact.linkedin_url) === normalizedLinkedInUrl;
      const sameNameAndCompany =
        normalizeName(contact.name) === normalizedName &&
        getEntityId(contact, ['company_id']) === companyId;

      return sameLinkedInUrl || sameNameAndCompany;
    });

    let contact = existingContact;
    let contactId = getEntityId(contact, ['contact_id', 'id']);

    if (!contactId) {
      const contactResult = await addContact({
        company_id: companyId,
        name: prospect.full_name,
        title: prospect.title || '',
        role: prospect.title || '',
        linkedin_url: prospect.linkedin_url || '',
        email: '',
        phone: '',
        role_in_buying: '',
        score: 0
      });

      if (!contactResult?.success) {
        return {
          success: false,
          error: getErrorMessage(contactResult?.error) || 'Kontakten kunne ikke opprettes.'
        };
      }

      contact = contactResult.data;
      contactId = getEntityId(contact, ['contact_id', 'id']);

      if (!contactId) {
        return { success: false, error: 'Kontakten ble opprettet uten gyldig ID.' };
      }
    }

    const dealTitle = dealInput.title?.trim() || `LinkedIn-mulighet: ${prospect.full_name}`;
    const duplicateDeal = deals.find(deal => (
      getEntityId(deal, ['company_id']) === companyId &&
      getEntityId(deal, ['contact_id']) === contactId &&
      String(deal.title || '').trim() === dealTitle &&
      !isClosedDeal(deal)
    ));

    const duplicateLinkedInDeal = hasOpenLinkedInOpportunity({
      prospect,
      deals,
      companies: [...companies.filter(item => (
        getEntityId(item, ['company_id', 'id']) !== companyId
      )), company],
      contacts: [...contacts.filter(item => (
        getEntityId(item, ['contact_id', 'id']) !== contactId
      )), contact]
    });

    if (duplicateDeal || duplicateLinkedInDeal) {
      return {
        success: false,
        duplicate: true,
        error: 'Det finnes allerede en salgsmulighet for dette prospektet.'
      };
    }

    const dealResult = await addDeal({
      title: dealTitle,
      value: Number(dealInput.value) || 0,
      status: 'Åpen',
      probability: Number(dealInput.probability) || 10,
      company_id: companyId,
      contact_id: contactId,
      stage_id: dealInput.stage_id || 'stage-1',
      source: 'LinkedIn Outreach',
      notes: dealInput.notes || '',
      companyName,
      company: companyName,
      contactName: prospect.full_name,
      jobTitle: prospect.title || '',
      owner_user_id: prospect.owner_user_id || ''
    });

    if (!dealResult?.success) {
      return {
        success: false,
        error: getErrorMessage(dealResult?.error) || 'Salgsmuligheten kunne ikke opprettes.'
      };
    }

    return {
      success: true,
      data: {
        company,
        contact,
        deal: dealResult.data
      }
    };
  } catch (error) {
    return {
      success: false,
      error: getErrorMessage(error) || 'Salgsmuligheten kunne ikke opprettes.'
    };
  }
};